"use client";

import React, { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { en } from "./locales/en";
import { mr } from "./locales/mr";

export type Language = "en" | "mr";

type Translations = typeof en;

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
  isMarathi: boolean;
  getLocalized: <T>(enVal: T, mrVal?: T | null) => T;
}

const translations: Record<Language, Translations> = { en, mr };

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "mmmocl_lang";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>("en");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
      if (stored === "en" || stored === "mr") {
        setLangState(stored);
        document.documentElement.lang = stored;
      }
    } catch {
      // localStorage may fail in restricted/incognito environments
    }
    setMounted(true);
  }, []);

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    try {
      localStorage.setItem(STORAGE_KEY, newLang);
      document.documentElement.lang = newLang;
    } catch {
      // ignore storage failure
    }
  };

  const getLocalized = <T,>(enVal: T, mrVal?: T | null): T => {
    if (lang === "mr" && mrVal !== undefined && mrVal !== null && mrVal !== "") {
      return mrVal;
    }
    return enVal;
  };

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        t: translations[lang],
        isMarathi: lang === "mr",
        getLocalized,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return fallback for SSR or pre-mount
    return {
      lang: "en" as Language,
      setLang: () => {},
      t: en,
      isMarathi: false,
      getLocalized: <T,>(enVal: T, mrVal?: T | null) => (mrVal || enVal),
    };
  }
  return context;
}
